import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthResponse, User } from '@conversia/shared-types';
import * as authApi from '@/api/auth.api';

/**
 * Estado de autenticación persistido en localStorage. El cliente HTTP
 * (`api/client.ts`) lee `accessToken` y, en 401, llama a `setSession`/
 * `clearSession` tras renovar — por eso esas acciones viven aquí y no en un
 * efecto de React.
 */
interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  status: 'idle' | 'loading' | 'error';
  error: string | null;

  isAuthenticated: () => boolean;
  setSession: (session: AuthResponse) => void;
  clearSession: () => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  loadMe: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      status: 'idle',
      error: null,

      isAuthenticated: () => Boolean(get().accessToken && get().user),

      setSession: (session) =>
        set({
          user: session.user,
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
          status: 'idle',
          error: null,
        }),

      clearSession: () =>
        set({ user: null, accessToken: null, refreshToken: null, status: 'idle' }),

      login: async (email, password) => {
        set({ status: 'loading', error: null });
        try {
          const session = await authApi.login({ email, password });
          get().setSession(session);
        } catch (err) {
          set({ status: 'error', error: (err as Error).message });
          throw err;
        }
      },

      logout: async () => {
        const token = get().refreshToken;
        if (token) await authApi.logout(token).catch(() => undefined);
        get().clearSession();
      },

      refresh: async () => {
        const token = get().refreshToken;
        if (!token) return;
        const session = await authApi.refresh(token);
        get().setSession(session);
      },

      loadMe: async () => {
        if (!get().accessToken) return;
        try {
          const user = await authApi.me();
          set({ user });
        } catch {
          get().clearSession();
        }
      },
    }),
    {
      name: 'conversia.auth',
      partialize: (s) => ({
        user: s.user,
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
      }),
    },
  ),
);
